import { formatForDisplay, formatToLocalTimeString } from '@/lib/time'
import { copy } from '@/copy/strings'

interface StatusBadgeProps {
  isSynced: boolean
  processingStatus: string
  syncedAt: string | null | undefined
  onRetrySync: () => void
}

export function StatusBadge({ isSynced, processingStatus, syncedAt, onRetrySync }: StatusBadgeProps) {
  if (isSynced && processingStatus === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-600/20 px-3 py-1 text-xs font-semibold text-red-300 border border-red-500/40"
        aria-label={copy.statusUploadDidNotFinish}
      >
        <span>{copy.statusUploadDidNotFinish}</span>
      </span>
    )
  }

  // A server-side lifecycle like 'queued' / 'processing' is a backend detail
  // that isn't meaningful to end users — once the upload leaves the device,
  // we show a single "Uploaded" state.
  if (isSynced) {
    const uploadedTime = syncedAt
      ? formatForDisplay(formatToLocalTimeString(syncedAt))
      : ''
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-green-600/20 px-3 py-1 text-xs font-semibold text-green-300 border border-green-500/40"
        aria-label={uploadedTime ? `${copy.statusUploaded} at ${uploadedTime}` : copy.statusUploaded}
      >
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M16.704 5.29a1 1 0 010 1.415l-7.18 7.176a1 1 0 01-1.414 0L3.296 8.066a1 1 0 011.414-1.415l4.1 4.1 6.472-6.461a1 1 0 011.422 0z"
            clipRule="evenodd"
          />
        </svg>
        <span>
          {copy.statusUploaded}
          {uploadedTime ? (
            <>
              {' '}
              <span className="sr-only">at </span>
              {uploadedTime}
            </>
          ) : null}
        </span>
      </span>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-3 py-1 text-xs font-semibold text-yellow-200 border border-yellow-500/40"
        aria-label={copy.statusWaitingToUpload}
      >
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M11 3.055a7 7 0 016 6.92V11a7 7 0 11-14 0v-.955a7 7 0 016-6.92V1a1 1 0 112 0v2.055zM10 6a1 1 0 00-1 1v4a1 1 0 102 0V7a1 1 0 00-1-1zm0 8a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z"
            clipRule="evenodd"
          />
        </svg>
        <span>{copy.statusWaitingToUpload}</span>
      </span>
      <button
        onClick={onRetrySync}
        className="text-xs font-semibold text-neon-cyan hover:text-neon-cyan/80 underline focus:outline-none focus:ring-2 focus:ring-neon-cyan rounded px-1"
      >
        {copy.micRetry}
        <span className="sr-only"> sync for this recording</span>
      </button>
    </div>
  )
}
